// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, Typography, Box, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LanguageIcon from '@mui/icons-material/Language';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CampaignIcon from '@mui/icons-material/Campaign';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TableChartIcon from '@mui/icons-material/TableChart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import TodayIcon from '@mui/icons-material/Today';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getCurrentUser } from '../services/userService';

const drawerWidth = 240;

const Sidebar = ({ mobileOpen, onDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  // Build menu based on user role
  const getMenuItems = () => {
    switch (user?.role) {
      case 'admin':
        return [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
          { text: 'Việc của tôi', icon: <TodayIcon />, path: '/admin/my-work' },
          { text: 'Personal To-Do', icon: <PlaylistAddCheckIcon />, path: '/admin/personal-todos' },
          { text: 'Dự án', icon: <FolderIcon />, children: [
            { text: 'Projects', icon: <FolderIcon />, path: '/admin/projects' },
            { text: 'Tổng quan dự án', icon: <DashboardIcon />, path: '/admin/portfolio' },
          ]},
          { text: 'Công việc', icon: <AssignmentIcon />, children: [
            { text: 'Tasks', icon: <AssignmentIcon />, path: '/admin/tasks' },
            { text: 'Task Performance', icon: <TimelineIcon />, path: '/admin/task-performance' },
            { text: 'Phân bổ công việc', icon: <PeopleIcon />, path: '/admin/workload' },
          ]},
          { text: 'Đề xuất Chi phí', icon: <AttachMoneyIcon />, path: '/admin/expense-proposals' },
          { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
          { text: 'Marketing', icon: <CampaignIcon />, children: [
            { text: 'Marketing Dashboard', icon: <CampaignIcon />, path: '/admin/marketing' },
            { text: 'Command Center (CRM)', icon: <MonetizationOnIcon />, path: '/admin/marketing/command-center' },
          ]},
          { text: 'SEO', icon: <LanguageIcon />, children: [
            { text: 'SEO Command Center', icon: <AnalyticsIcon />, path: '/admin/seo-overview' },
            { text: 'Websites & Keywords', icon: <LanguageIcon />, path: '/admin/seo' },
            { text: 'SEO Health', icon: <HealthAndSafetyIcon />, path: '/admin/seo-health' },
          ]},
          { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
          { text: 'Thưởng Marketing', icon: <MonetizationOnIcon />, path: '/admin/marketing-bonus' },
          { text: 'Mindmap', icon: <AccountTreeIcon />, path: '/admin/mindmap' },
          { text: 'Google Sheets', icon: <TableChartIcon />, path: '/admin/google-sheets' },
          { text: 'KPI Assignments', icon: <PsychologyIcon />, path: '/admin/kpi-assignments' },
          { text: 'Alerts', icon: <NotificationsIcon />, path: '/admin/alerts' },
          { text: 'Notifications', icon: <NotificationsIcon />, path: '/admin/notifications' },
          { text: 'Kho Lưu Trữ', icon: <MenuBookIcon />, path: '/admin/knowledge' },
          { text: 'AI Configuration', icon: <SettingsIcon />, path: '/admin/ai-config' },
          { text: 'Quỹ phòng', icon: <AccountBalanceIcon />, path: '/admin/fund' },
          { text: 'Quản Lý Tài Khoản', icon: <VpnKeyIcon />, path: '/admin/accounts' },
        ];
      case 'leader':
        return [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
          { text: 'Việc của tôi', icon: <TodayIcon />, path: '/admin/my-work' },
          { text: 'Personal To-Do', icon: <PlaylistAddCheckIcon />, path: '/admin/personal-todos' },
          { text: 'Dự án', icon: <FolderIcon />, children: [
            { text: 'Projects', icon: <FolderIcon />, path: '/admin/projects' },
            { text: 'Tổng quan dự án', icon: <DashboardIcon />, path: '/admin/portfolio' },
          ]},
          { text: 'Công việc', icon: <AssignmentIcon />, children: [
            { text: 'Tasks', icon: <AssignmentIcon />, path: '/admin/tasks' },
            { text: 'Task Performance', icon: <TimelineIcon />, path: '/admin/task-performance' },
            { text: 'Phân bổ công việc', icon: <PeopleIcon />, path: '/admin/workload' },
          ]},
          { text: 'Đề xuất Chi phí', icon: <AttachMoneyIcon />, path: '/admin/expense-proposals' },
          { text: 'SEO', icon: <LanguageIcon />, children: [
            { text: 'SEO Command Center', icon: <AnalyticsIcon />, path: '/admin/seo-overview' },
            { text: 'Websites & Keywords', icon: <LanguageIcon />, path: '/admin/seo' },
            { text: 'SEO Health', icon: <HealthAndSafetyIcon />, path: '/admin/seo-health' },
          ]},
          { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
          { text: 'Marketing', icon: <CampaignIcon />, children: [
            { text: 'Marketing Dashboard', icon: <CampaignIcon />, path: '/admin/marketing' },
            { text: 'Command Center (CRM)', icon: <MonetizationOnIcon />, path: '/admin/marketing/command-center' },
          ]},
          { text: 'Thưởng Marketing', icon: <MonetizationOnIcon />, path: '/admin/marketing-bonus' },
          { text: 'Mindmap', icon: <AccountTreeIcon />, path: '/admin/mindmap' },
          { text: 'Google Sheets', icon: <TableChartIcon />, path: '/admin/google-sheets' },
          { text: 'My KPI Assignments', icon: <RateReviewIcon />, path: '/admin/my-kpi-assignments' },
          { text: 'Kho Lưu Trữ', icon: <MenuBookIcon />, path: '/admin/knowledge' },
          { text: 'Quỹ phòng', icon: <AccountBalanceIcon />, path: '/admin/fund' },
          { text: 'Quản Lý Tài Khoản', icon: <VpnKeyIcon />, path: '/admin/accounts' },
        ];
      case 'member':
      default:
        return [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
          { text: 'Việc của tôi', icon: <TodayIcon />, path: '/admin/my-work' },
          { text: 'Personal To-Do', icon: <PlaylistAddCheckIcon />, path: '/admin/personal-todos' },
          { text: 'Dự án', icon: <FolderIcon />, children: [
            { text: 'Projects', icon: <FolderIcon />, path: '/admin/projects' },
          ]},
          { text: 'Công việc', icon: <AssignmentIcon />, children: [
            { text: 'Tasks', icon: <AssignmentIcon />, path: '/admin/tasks' },
            { text: 'Task Performance', icon: <TimelineIcon />, path: '/admin/task-performance' },
          ]},
          { text: 'Đề xuất Chi phí', icon: <AttachMoneyIcon />, path: '/admin/expense-proposals' },
          { text: 'SEO', icon: <LanguageIcon />, children: [
            { text: 'SEO Command Center', icon: <AnalyticsIcon />, path: '/admin/seo-overview' },
            { text: 'Websites & Keywords', icon: <LanguageIcon />, path: '/admin/seo' },
            { text: 'SEO Health', icon: <HealthAndSafetyIcon />, path: '/admin/seo-health' },
          ]},
          { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
          { text: 'Marketing CRM', icon: <MonetizationOnIcon />, path: '/admin/marketing/command-center' },
          { text: 'Mindmap', icon: <AccountTreeIcon />, path: '/admin/mindmap' },
          { text: 'Google Sheets', icon: <TableChartIcon />, path: '/admin/google-sheets' },
          { text: 'Kho Lưu Trữ', icon: <MenuBookIcon />, path: '/admin/knowledge' },
          { text: 'Quỹ phòng', icon: <AccountBalanceIcon />, path: '/admin/fund' },
          { text: 'Quản Lý Tài Khoản', icon: <VpnKeyIcon />, path: '/admin/accounts' },
        ];
    }
  };

  const menuItems = getMenuItems();

  // Submenu mở/đóng - tự mở nhóm chứa route hiện tại
  const [openGroups, setOpenGroups] = useState({});
  useEffect(() => {
    const next = {};
    menuItems.forEach((item) => {
      if (item.children && item.children.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))) {
        next[item.text] = true;
      }
    });
    setOpenGroups((prev) => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  const toggleGroup = (text) => setOpenGroups((prev) => ({ ...prev, [text]: !prev[text] }));

  // Khớp chính xác hoặc là route con (tránh '/admin/seo' khớp nhầm '/admin/seo-overview')
  const isActive = (path) =>
    !!path && (location.pathname === path || location.pathname.startsWith(path + '/'));

  const itemHoverSx = {
    '&:hover': {
      backgroundColor: 'primary.light',
      '& .MuiListItemIcon-root': { color: 'primary.main' },
      '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 'medium' },
    },
  };

  const drawer = (
    <React.Fragment>
      <Toolbar />
      <Divider />
      {/* User Info */}
      {user && (
        <Box sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.role === 'admin' ? 'Administrator' : 
             user.role === 'leader' ? 'Team Leader' : 
             user.role === 'employee' ? 'Employee' : 'User'}
          </Typography>
        </Box>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => {
          // Nhóm có submenu
          if (item.children) {
            const open = !!openGroups[item.text];
            const groupActive = item.children.some((c) => isActive(c.path));
            return (
              <React.Fragment key={item.text}>
                <ListItemButton onClick={() => toggleGroup(item.text)} sx={itemHoverSx}>
                  <ListItemIcon sx={{ color: groupActive ? 'primary.main' : 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontWeight: groupActive ? 'bold' : 'regular' }}
                  />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.text}
                        onClick={() => navigate(child.path)}
                        selected={isActive(child.path)}
                        sx={{ pl: 4, ...itemHoverSx }}
                      >
                        <ListItemIcon sx={{ color: isActive(child.path) ? 'primary.main' : 'inherit' }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }
          // Mục đơn
          return (
            <ListItemButton
              key={item.text}
              onClick={() => navigate(item.path)}
              selected={isActive(item.path)}
              sx={itemHoverSx}
            >
              <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          );
        })}
        <Divider sx={{ my: 1 }} />
        <ListItemButton onClick={() => navigate('/admin/user-guide')}>
          <ListItemIcon><MenuBookIcon /></ListItemIcon>
          <ListItemText primary="Hướng dẫn sử dụng" />
        </ListItemButton>
      </List>
    </React.Fragment>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};

Sidebar.propTypes = {
  mobileOpen: PropTypes.bool,
  onDrawerToggle: PropTypes.func,
};

export default Sidebar;
